// Request Context Panel (VAC-8)
// Expandable drawer showing comprehensive request details, employee balance, and team coverage

import React, { useState } from 'react'
import { X, User, Calendar, Clock, AlertCircle, Users } from 'lucide-react'
import { format, formatDistanceToNow, differenceInDays } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEmployeeBalance, useVacationCalendar } from '@/hooks/useApprovalQueue'
import type { ApprovalQueueItem } from '@/types/vacation'

interface RequestContextPanelProps {
  request: ApprovalQueueItem | null
  isOpen: boolean
  onClose: () => void
  onApprove: (requestId: string) => void
  onReject: (requestId: string) => void
}

export const RequestContextPanel: React.FC<RequestContextPanelProps> = ({
  request,
  isOpen,
  onClose,
  onApprove,
  onReject
}) => {
  const [approvalNotes, setApprovalNotes] = useState('')
  const [rejectionReason, setRejectionReason] = useState('')
  const [showApprovalForm, setShowApprovalForm] = useState(false)
  const [showRejectionForm, setShowRejectionForm] = useState(false)

  // Fetch employee balance if request is available
  const { data: balanceData, isLoading: balanceLoading } = useEmployeeBalance(
    request?.employeeId || '',
    !!request
  )

  // Fetch team calendar for overlap detection
  const { data: teamCalendarData } = useVacationCalendar(
    request?.startDate || '',
    request?.endDate || '',
    undefined,
    'APPROVED',
    !!request
  )

  if (!isOpen || !request) return null

  const handleApproveClick = () => {
    if (showApprovalForm) {
      onApprove(request.requestId)
      setApprovalNotes('')
      setShowApprovalForm(false)
    } else {
      setShowApprovalForm(true)
      setShowRejectionForm(false)
    }
  }

  const handleRejectClick = () => {
    if (showRejectionForm && rejectionReason.trim().length >= 10) {
      onReject(request.requestId)
      setRejectionReason('')
      setShowRejectionForm(false)
    } else {
      setShowRejectionForm(true)
      setShowApprovalForm(false)
    }
  }

  const handleCancel = () => {
    setShowApprovalForm(false)
    setShowRejectionForm(false)
    setApprovalNotes('')
    setRejectionReason('')
  }

  const vacationTypeLabel = {
    FULL: 'Férias Completas',
    SPLIT: 'Férias Fracionadas',
    SELL_THIRD: 'Venda de 1/3'
  }[request.type]

  // Calculate team overlap
  const overlappingEmployees = teamCalendarData?.items.filter(
    entry => entry.employeeId !== request.employeeId
  ) || []

  const totalTeamMembers = 10 // TODO: Get from team data
  const overlapPercentage = (overlappingEmployees.length / totalTeamMembers) * 100
  const hasHighOverlap = overlapPercentage > 30

  // Balance expiration days
  const daysUntilExpiration = balanceData?.expirationDate
    ? differenceInDays(new Date(balanceData.expirationDate), new Date())
    : null

  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-overlay"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-brand-dark-light shadow-monumenta overflow-y-auto animate-slide-in-right z-50">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-display font-bold text-white">
              Detalhes da Solicitação
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-brand-dark-lighter rounded-monumenta transition-colors"
              aria-label="Fechar"
            >
              <X className="w-5 h-5 text-brand-gray hover:text-white" />
            </button>
          </div>

          {/* Employee Details Section */}
          <section className="mb-6 p-4 bg-brand-dark-lighter rounded-monumenta">
            <div className="flex items-center gap-2 mb-3">
              <User className="w-5 h-5 text-brand-primary" />
              <h3 className="text-lg font-display font-semibold text-white">
                Informações do Colaborador
              </h3>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                {request.employeeAvatar ? (
                  <img
                    src={request.employeeAvatar}
                    alt={request.employeeName}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-500 flex items-center justify-center text-gray-900 font-semibold">
                    {request.employeeName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div>
                  <p className="font-display font-semibold text-white">
                    {request.employeeName}
                  </p>
                  <p className="text-sm text-brand-gray">
                    {request.employeeDepartment || 'Departamento não informado'}
                  </p>
                </div>
              </div>
              {request.employeeRole && (
                <p className="text-sm text-brand-gray">
                  <span className="font-medium text-white">Cargo:</span> {request.employeeRole}
                </p>
              )}
            </div>
          </section>

          {/* Request Details Section */}
          <section className="mb-6 p-4 bg-brand-dark-lighter rounded-monumenta">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-5 h-5 text-brand-primary" />
              <h3 className="text-lg font-display font-semibold text-white">
                Detalhes da Solicitação
              </h3>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-brand-gray mb-1">Período</p>
                <p className="font-display font-semibold text-white">
                  {format(new Date(request.startDate), "dd 'de' MMMM 'de' yyyy (EEEE)", { locale: ptBR })}
                  <span className="mx-2">→</span>
                  {format(new Date(request.endDate), "dd 'de' MMMM 'de' yyyy (EEEE)", { locale: ptBR })}
                </p>
              </div>
              <div>
                <p className="text-sm text-brand-gray mb-1">Total de dias</p>
                <p className="font-display font-semibold text-white">
                  {request.totalDays} dias úteis
                </p>
              </div>
              <div>
                <p className="text-sm text-brand-gray mb-1">Tipo de férias</p>
                <span className="inline-block px-3 py-1 bg-info/10 border border-info/30 text-info text-sm font-medium rounded-full">
                  {vacationTypeLabel}
                </span>
              </div>
              <div>
                <p className="text-sm text-brand-gray mb-1">Enviado</p>
                <p className="text-sm text-white">
                  {formatDistanceToNow(new Date(request.submittedAt), { locale: ptBR, addSuffix: true })}
                  <span className="text-brand-gray ml-1">
                    ({format(new Date(request.submittedAt), "dd/MM/yyyy 'às' HH:mm")})
                  </span>
                </p>
              </div>
              {request.employeeNotes && (
                <div>
                  <p className="text-sm text-brand-gray mb-1">Observações do colaborador</p>
                  <p className="text-sm text-white bg-brand-dark p-3 rounded-monumenta border border-brand-gray/20">
                    {request.employeeNotes}
                  </p>
                </div>
              )}
            </div>
          </section>

          {/* Balance Information Section */}
          <section className="mb-6 p-4 bg-brand-dark-lighter rounded-monumenta">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5 text-brand-primary" />
              <h3 className="text-lg font-display font-semibold text-white">
                Saldo de Férias
              </h3>
            </div>
            {balanceLoading ? (
              <div className="flex items-center justify-center py-4">
                <svg className="animate-spin h-6 w-6 text-brand-primary" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            ) : balanceData ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-brand-gray">Período aquisitivo</p>
                    <p className="font-display font-semibold text-white">
                      {balanceData.periodYear}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-brand-gray">Dias adquiridos</p>
                    <p className="font-display font-semibold text-white">
                      {balanceData.totalDaysEarned || balanceData.daysEarned || 30} dias
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-brand-gray">Dias utilizados</p>
                    <p className="font-display font-semibold text-white">
                      {balanceData.daysUsed || 0} dias
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-brand-gray">Dias disponíveis</p>
                    <p className="font-display font-semibold text-success">
                      {balanceData.daysAvailable || 30} dias
                    </p>
                  </div>
                </div>
                {daysUntilExpiration !== null && daysUntilExpiration < 60 && (
                  <div className="flex items-start gap-2 p-3 bg-warning/10 border border-warning/30 rounded-monumenta">
                    <AlertCircle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-display font-medium text-warning">
                        Saldo expirando em breve
                      </p>
                      <p className="text-sm text-white">
                        {daysUntilExpiration} dias até {format(new Date(balanceData.expirationDate), "dd/MM/yyyy")}
                      </p>
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-sm text-brand-gray mb-1">Saldo após aprovação</p>
                  <p className="text-lg font-display font-bold text-white">
                    {(balanceData.daysAvailable || 30) - request.totalDays} dias restantes
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-brand-gray">
                Não foi possível carregar o saldo de férias
              </p>
            )}
          </section>

          {/* Team Coverage Section */}
          <section className="mb-6 p-4 bg-brand-dark-lighter rounded-monumenta">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-5 h-5 text-brand-primary" />
              <h3 className="text-lg font-display font-semibold text-white">
                Cobertura da Equipe
              </h3>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-brand-gray mb-2">
                  Colaboradores em férias no mesmo período
                </p>
                {overlappingEmployees.length > 0 ? (
                  <div className="space-y-2">
                    {overlappingEmployees.slice(0, 5).map(entry => (
                      <div key={entry.requestId} className="flex items-center gap-2 text-sm">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-brand-primary to-brand-primary/80 flex items-center justify-center text-white text-xs font-semibold">
                          {entry.employeeName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                        </div>
                        <span className="text-white font-medium">
                          {entry.employeeName}
                        </span>
                        <span className="text-brand-gray">
                          ({entry.totalDays} dias)
                        </span>
                      </div>
                    ))}
                    {overlappingEmployees.length > 5 && (
                      <p className="text-sm text-brand-gray italic">
                        +{overlappingEmployees.length - 5} outros...
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-brand-gray">
                    Nenhum outro colaborador em férias neste período
                  </p>
                )}
              </div>
              {hasHighOverlap && (
                <div className="flex items-start gap-2 p-3 bg-error/10 border border-error/30 rounded-monumenta">
                  <AlertCircle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-display font-medium text-error">
                      Atenção: Alta sobreposição de férias
                    </p>
                    <p className="text-sm text-white">
                      {overlapPercentage.toFixed(0)}% da equipe em férias simultaneamente
                    </p>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Action Buttons */}
          <div className="sticky bottom-0 bg-brand-dark-light border-t border-brand-gray/20 pt-4 -mx-6 px-6 pb-6">
            {showApprovalForm && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-white mb-2">
                  Observações da aprovação (opcional)
                </label>
                <textarea
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  maxLength={500}
                  rows={3}
                  className="w-full px-3 py-2 border border-brand-gray/20 rounded-monumenta bg-brand-dark-lighter text-white text-sm resize-none focus:ring-2 focus:ring-success focus:border-transparent placeholder-brand-gray"
                  placeholder="Adicione observações sobre a aprovação (opcional)"
                />
                <p className="text-xs text-brand-gray mt-1">
                  {approvalNotes.length}/500 caracteres
                </p>
              </div>
            )}

            {showRejectionForm && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-white mb-2">
                  Motivo da rejeição <span className="text-error">*</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  maxLength={500}
                  rows={3}
                  className="w-full px-3 py-2 border border-brand-gray/20 rounded-monumenta bg-brand-dark-lighter text-white text-sm resize-none focus:ring-2 focus:ring-error focus:border-transparent placeholder-brand-gray"
                  placeholder="Explique o motivo da rejeição (mínimo 10 caracteres)"
                />
                <p className="text-xs text-brand-gray mt-1">
                  {rejectionReason.length}/500 caracteres (mínimo 10)
                </p>
                {showRejectionForm && rejectionReason.length > 0 && rejectionReason.length < 10 && (
                  <p className="text-xs text-error mt-1">
                    O motivo deve ter pelo menos 10 caracteres
                  </p>
                )}
              </div>
            )}

            <div className="flex gap-3">
              {!showApprovalForm && !showRejectionForm && (
                <>
                  <button
                    onClick={handleApproveClick}
                    className="btn-primary flex-1"
                  >
                    Aprovar
                  </button>
                  <button
                    onClick={handleRejectClick}
                    className="btn-danger flex-1"
                  >
                    Rejeitar
                  </button>
                  <button
                    onClick={onClose}
                    className="btn-secondary"
                  >
                    Fechar
                  </button>
                </>
              )}

              {(showApprovalForm || showRejectionForm) && (
                <>
                  <button
                    onClick={showApprovalForm ? handleApproveClick : handleRejectClick}
                    disabled={showRejectionForm && rejectionReason.trim().length < 10}
                    className={`flex-1 ${showApprovalForm ? 'btn-primary' : 'btn-danger'}`}
                  >
                    {showApprovalForm ? 'Confirmar Aprovação' : 'Confirmar Rejeição'}
                  </button>
                  <button
                    onClick={handleCancel}
                    className="btn-secondary"
                  >
                    Cancelar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
